import { Suspense, Fragment, lazy } from 'react';
import { Switch, Redirect, Route } from 'react-router-dom';
import DashboardLayout from 'src/layouts/DashboardLayout';
import LoadingScreen from 'src/components/LoadingScreen';
import AuthGuard from 'src/guards/AuthGuard';
import GuestGuard from 'src/guards/GuestGuard';
import NewUserAuthGuard from 'src/guards/NewUserAuthGuard';
import UserPayingGuard from './guards/UserPayingGuard';
import {
  DummyBusinessPricingGuard,
  DummyBusinessInitializationGuard,
} from 'src/guards/DummyBusinessGuard';
import SignedInGuard from './guards/SignedInGuard';
import UpgradingGuard from './guards/UpgradingGuard';

type Routes = {
  exact?: boolean;
  path?: string | string[];
  guard?: any;
  layout?: any;
  component?: any;
  routes?: Routes;
}[];

export const renderRoutes = (routes: Routes = []): JSX.Element => (
  <Suspense fallback={<LoadingScreen />}>
    <Switch>
      {routes.map((route, i) => {
        let Guards: any[] = [];
        if (Array.isArray(route.guard)) {
          Guards = route.guard;
        } else if (route.guard) {
          Guards = [route.guard];
        }
        const Layout = route.layout || Fragment;
        const Component = route.component;

        // Wrap the component in all guards, outermost first
        let WrappedComponent = (props: any) => (
          <Layout>
            {route.routes ? (
              renderRoutes(route.routes)
            ) : (
              <Component {...props} />
            )}
          </Layout>
        );
        Guards.forEach((Guard) => {
          const Prev = WrappedComponent;
          WrappedComponent = (props: any) => (
            <Guard>
              <Prev {...props} />
            </Guard>
          );
        });

        return (
          <Route
            key={i}
            path={route.path}
            exact={route.exact}
            render={WrappedComponent}
          />
        );
      })}
    </Switch>
  </Suspense>
);

const routes: Routes = [
  {
    exact: true,
    path: '/404',
    component: lazy(() => import('src/views/errors/NotFoundView')),
  },

  {
    exact: true,
    guard: UpgradingGuard,
    path: '/upgrading',
    component: lazy(() => import('src/views/errors/UpgradingView')),
  },

  {
    exact: true,
    path: '/fallback',
    component: lazy(() => import('src/views/errors/FallbackUI')),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/login',
    component: lazy(() => import('src/views/auth/LoginView')),
  },
  {
    exact: true,
    path: '/login-unprotected',
    component: lazy(() => import('src/views/auth/LoginView')),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/reset-password',
    component: lazy(() => import('src/views/auth/ResetPasswordView')),
  },
  {
    exact: true,
    path: '/verify-email',
    component: lazy(() => import('src/views/auth/VerifyEmailView')),
  },
  {
    exact: true,
    path: '/login-invite/:companyId/:userEmail/:businessName',
    component: lazy(() => import('src/views/auth/LoginAndInviteView')),
  },
  {
    exact: true,
    guard: SignedInGuard,
    path: '/accept-invitation/form/:companyId/:userEmail/:businessName',
    component: lazy(() => import('src/views/shared-access/NewUserForm')),
  },
  {
    // OAuth consent screen for connecting an MCP client (e.g. Claude). Reached
    // via a browser redirect from the backend's /oauth/authorize; requires a
    // signed-in session (the backend binds the minted code to this user).
    exact: true,
    guard: SignedInGuard,
    path: '/mcp/consent',
    component: lazy(() => import('src/views/mcp/ConsentView')),
  },
  {
    exact: true,
    guard: [DummyBusinessPricingGuard, SignedInGuard],
    path: '/app/create-business',
    component: lazy(
      () => import('src/views/account/AccountView/NewCompanyForm')
    ),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/register',
    component: lazy(() => import('src/views/auth/RegisterView')),
  },
  {
    exact: true,
    path: '/register-unprotected',
    component: lazy(() => import('src/views/auth/RegisterView')),
  },
  {
    exact: true,
    guard: [NewUserAuthGuard, DummyBusinessInitializationGuard],
    path: '/app/account/form',
    component: lazy(() => import('src/views/account/AccountView/NewUserForm')),
  },
  {
    exact: true,
    guard: [UserPayingGuard, DummyBusinessInitializationGuard],
    path: '/app/pricing',
    component: lazy(() => import('src/views/pricing/PricingView')),
  },
  {
    exact: true,
    path: '/view-receipt/:receiptId',
    component: lazy(
      () => import('src/views/financial-statements/ViewReceiptPage')
    ),
  },

  {
    // Full-screen experience, so it sits outside the dashboard layout.
    exact: true,
    path: '/app/wrapped',
    guard: AuthGuard,
    component: lazy(() => import('src/views/wrapped')),
  },
  {
    path: '/app',
    guard: AuthGuard,
    layout: DashboardLayout,
    routes: [
      {
        exact: true,
        path: '/app/account',
        component: lazy(() => import('src/views/account/AccountView')),
      },
      {
        exact: true,
        path: '/app/profile',
        component: lazy(() => import('src/views/profile/AccountView')),
      },
      {
        exact: true,
        path: '/app/account/bank',
        component: lazy(() => import('src/views/account/AccountView')),
      },
      {
        exact: true,
        path: '/app/account/add-bank',
        component: lazy(
          () => import('src/views/account/AccountView/AddBankFromStatement')
        ),
      },
      {
        exact: true,
        path: '/app/account/statement-uploads-business',
        component: lazy(
          () => import('src/views/account/AccountView/StatementUploads')
        ),
      },
      {
        exact: true,
        path: '/app/account/statement-uploads/:bankId',
        component: lazy(
          () => import('src/views/account/AccountView/StatementUploads')
        ),
      },
      {
        exact: true,
        path: '/app/account/guide',
        component: lazy(
          () =>
            import('src/views/account/AccountView/Guides/BankConnectedGuide')
        ),
      },
      {
        exact: true,
        path: '/app/account/ticket',
        component: lazy(() => import('src/views/account/AccountView/Ticket')),
      },
      {
        exact: true,
        path: '/app/account/duplicate-bank-error',
        component: lazy(
          () => import('src/views/account/AccountView/DuplicateBankError')
        ),
      },
      {
        exact: true,
        path: '/app/account/bank-disconnected',
        component: lazy(
          () => import('src/views/account/AccountView/BankDisconnected')
        ),
      },
      {
        exact: true,
        path: '/app/account/relink-success',
        component: lazy(
          () => import('src/views/account/AccountView/Guides/RelinkSuccess')
        ),
      },
      {
        exact: true,
        path: '/app/account/account-change',
        component: lazy(
          () =>
            import('src/views/account/AccountView/Guides/AccountChangeSuccess')
        ),
      },
      {
        exact: true,
        path: ['/app/chat/new', '/app/chat/:threadKey'],
        component: lazy(() => import('src/views/chat/ChatView')),
      },
      {
        exact: true,
        path: '/app/chat',
        component: () => <Redirect to="/app/chat/new" />,
      },
      {
        exact: true,
        path: '/app/management/invoices',
        component: lazy(() => import('src/views/invoice/InvoiceListView')),
      },
      {
        exact: true,
        path: '/app/management/receipts',
        component: lazy(() => import('src/views/shoebox/ReceiptListView')),
      },
      {
        exact: true,
        path: '/app/management/connect-stripe',
        component: lazy(
          () =>
            //import('src/views/stripe/StripeAuthorizationView')
            import('src/views/stripe/StripeGuide/index')
        ),
      },
      {
        exact: true,
        path: '/app/management/taxes',
        component: lazy(() => import('src/views/taxes/index')),
      },
      {
        exact: true,
        path: '/app/management/transactions',
        component: lazy(
          () => import('src/views/transaction/TransactionListView')
        ),
      },
      {
        exact: true,
        path: '/app/management',
        component: () => <Redirect to="/app/management/customers" />,
      },
      {
        exact: true,
        path: '/app/automation/overview',
        component: lazy(() => import('src/views/automation/OverviewView')),
      },
      {
        exact: true,
        path: '/app/automation',
        component: () => <Redirect to="/app/automation/browse" />,
      },
      {
        exact: true,
        path: '/app/perks',
        component: lazy(
          () => import('src/views/recommendations/RecommendationsLibraryView')
        ),
      },
      {
        exact: true,
        path: [
          '/app/perks/library',
          '/app/recommendations',
          '/app/recommendations/library',
          '/app/recommendations/browse',
        ],
        component: () => <Redirect to="/app/perks" />,
      },
      {
        exact: true,
        path: '/app/reports/dashboard',
        component: lazy(() => import('src/views/reports/DashboardOverview')),
      },
      {
        exact: true,
        path: '/app/reports',
        component: () => <Redirect to="/app/reports/dashboard" />,
      },
      {
        exact: true,
        path: '/app',
        component: () => <Redirect to="/app/reports/dashboard" />,
      },
      {
        exact: true,
        path: '/app/coming-soon',
        component: lazy(() => import('src/views/errors/ComingSoon')),
      },
      {
        exact: true,
        path: '/app/aibookkeeper',
        component: lazy(
          () => import('src/views/aibookkeeper/AiBookkeeperIndexView')
        ),
      },
      {
        exact: true,
        path: '/app/aibookkeeper/success',
        component: lazy(
          () => import('src/views/aibookkeeper/AiBookkeeperGuide')
        ),
      },
      {
        exact: true,
        path: '/app/management/taxes/pendingreports',
        component: lazy(
          () => import('src/views/taxes/PendingSalesTaxReturn/index')
        ),
      },
      {
        exact: true,
        path: '/app/management/taxes/completedreports',
        component: lazy(
          () => import('src/views/taxes/CompletedSalesTaxReturn/index')
        ),
      },
      {
        exact: true,
        path: '/app/management/taxes/FileSalesTaxReport',
        component: lazy(
          () => import('src/views/taxes/FileSalesTaxReport/index')
        ),
      },
      {
        exact: true,
        path: '/app/management/taxes/FileSalesTaxReport/result',
        component: lazy(
          () => import('src/views/taxes/FileSalesTaxReport/BotResult')
        ),
      },
      // Canada
      {
        exact: true,
        path: '/app/management/taxes/income-tax-filing-options',
        component: lazy(
          () => import('src/views/taxes/Profile/IncomeTaxFilingOptions')
        ),
      },
      // USA
      {
        exact: true,
        path: '/app/management/income-tax-filing-options',
        component: lazy(
          () => import('src/views/taxes/Profile/IncomeTaxFilingOptions')
        ),
      },
      {
        exact: true,
        path: '/app/financial-statements',
        component: lazy(() => import('src/views/financial-statements/index')),
      },
      {
        exact: true,
        path: '/app/collaboration/accept-invitation/:companyId/',
        component: lazy(
          () => import('src/views/shared-access/AcceptInvite/index')
        ),
      },
      {
        exact: true,
        path: '/app/collaboration/choose-business/',
        component: lazy(
          () => import('src/views/shared-access/ChooseBusiness/index')
        ),
      },
      {
        exact: true,
        path: '/app/rewards',
        component: lazy(() => import('src/views/rewards/RewardsView')),
      },
      {
        exact: true,
        path: '/app/luca',
        component: lazy(() => import('src/views/aiSupport')),
      },
      {
        component: () => <Redirect to="/404" />,
      },
    ],
  },
  {
    path: '*',
    routes: [
      {
        exact: true,
        path: '/',
        component: () => <Redirect to="/login" />,
      },
      {
        component: () => <Redirect to="/404" />,
      },
    ],
  },
];

export default routes;
